import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  FeedbackContainer,
  SectionTitle,
  StarContainer,
  StyledRating,
  CommentBox,
  ButtonContainer,
  CancelButton,
  SubmitButton
} from './styles';
import {
  rateWorker,
  selectRateWorkerLoading,
  selectRateWorkerSuccess,
  selectRateWorkerError,
  selectRateWorkerData,
  updateWorkerRating,
  selectUpdateWorkerRatingSuccess,
  selectUpdateWorkerRatingLoading,
  selectUpdateWorkerRatingError,
  selectUpdateWorkerRatingMessage,
} from '../../../../../../../modules/worker';
import AlertPopup from '../../../../../../../components/common/AlertPopup';

interface PerformanceFeedbackProps {
  workerId: number;
  shiftId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

const PerformanceFeedback: React.FC<PerformanceFeedbackProps> = ({
  workerId,
  shiftId,
  onSuccess,
  onCancel
}) => {
  const dispatch = useDispatch<any>();

  const rateWorkerLoading = useSelector(selectRateWorkerLoading);
  const rateWorkerSuccess = useSelector(selectRateWorkerSuccess);
  const rateWorkerError = useSelector(selectRateWorkerError);
  const rateWorkerData = useSelector(selectRateWorkerData);

  const updateWorkerRatingLoading = useSelector(selectUpdateWorkerRatingLoading);
  const updateWorkerRatingSuccess = useSelector(selectUpdateWorkerRatingSuccess);
  const updateWorkerRatingError = useSelector(selectUpdateWorkerRatingError);
  const updateWorkerRatingMessage = useSelector(selectUpdateWorkerRatingMessage);

  const [rating, setRating] = useState<number | null>(0);
  const [comment, setComment] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');
  const [ratingId, setRatingId] = useState<number | null>(null);

  const handleRatingChange = (event: React.SyntheticEvent, newValue: number | null) => {
    setRating(newValue);
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  const handleSubmit = () => {
    if (ratingId) {
      dispatch(updateWorkerRating({
        ratingId,
        data: {
          rating: rating || 0,
          remarks: comment,
        }
      }));
    } else {
      dispatch(rateWorker({
        workerId,
        data: {
          rating: rating || 0,
          remarks: comment,
          shift_id: shiftId,
        }
      }));
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRating(0);
    setComment('');
    onCancel();
  };

  useEffect(() => {
    if (rateWorkerSuccess && rateWorkerData) {
      setRatingId(rateWorkerData.data.rating_id);
      setAlertMessage('Feedback submitted successfully');
      setAlertSeverity('success');
      setOpenAlert(true);
      onSuccess();
    }
  }, [rateWorkerSuccess, rateWorkerData, onSuccess]);

  useEffect(() => {
    if (rateWorkerError) {
      setAlertMessage(rateWorkerError || 'Failed to submit feedback');
      setAlertSeverity('error');
      setOpenAlert(true);
    }
  }, [rateWorkerError]);

  useEffect(() => {
    if (updateWorkerRatingSuccess) {
      setAlertMessage(updateWorkerRatingMessage || 'Feedback updated successfully');
      setAlertSeverity('success');
      setOpenAlert(true);
      onSuccess();
    }
  }, [updateWorkerRatingSuccess, updateWorkerRatingMessage, onSuccess]);

  useEffect(() => {
    if (updateWorkerRatingError) {
      setAlertMessage(updateWorkerRatingError || 'Failed to update feedback');
      setAlertSeverity('error');
      setOpenAlert(true);
    }
  }, [updateWorkerRatingError]);

  const loading = rateWorkerLoading || updateWorkerRatingLoading;

  return (
    <FeedbackContainer>
      <SectionTitle>Performance Feedback</SectionTitle>

      <StarContainer>
        <StyledRating
          name="performance-rating"
          value={rating}
          onChange={handleRatingChange}
          size="large"
          emptyIcon={<StarBorderIcon fontSize="inherit" />}
        />
      </StarContainer>

      <CommentBox
        placeholder="Add a comment..."
        multiline
        rows={4}
        variant="outlined"
        fullWidth
        value={comment}
        onChange={handleCommentChange}
      />

      <ButtonContainer>
        <CancelButton variant="outlined" onClick={handleCancel}>
          Cancel
        </CancelButton>
        <SubmitButton variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Submitting...' : (ratingId ? 'Update Feedback' : 'Submit Feedback')}
        </SubmitButton>
      </ButtonContainer>

      <AlertPopup
        open={openAlert}
        message={alertMessage}
        severity={alertSeverity}
        onClose={() => setOpenAlert(false)}
      />
    </FeedbackContainer>
  );
};

export default PerformanceFeedback;